import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import { getCurrentUser } from '../utils/auth';
import TruncatedText from '../components/common/TruncatedText';
import AdminNav from '../components/admin/AdminNav';
import Pagination from '../components/common/Pagination';
import ResultCount from '../components/common/ResultCount';
import OrderAmountModal from '../components/common/OrderAmountModal';
import UserInfoModal from '../components/common/UserInfoModal';
import { toast } from '../utils/toast';
import { nextSteps, isFinished } from '../utils/orderFlow';
import { useTranslation } from 'react-i18next';
import { animalLabel } from '../utils/animals';
import { stateLabel } from '../utils/usStates';
import { formatDeadline } from '../utils/formatDeadline';
import { canSupplierCancel, MIN_SUPPLIER_REASON, cancellationStatusKey, cancellationDetails, isPerOrderCash, legacyGroupNeedsReconcile } from '../utils/cancellationPolicy';
import { translateKnown } from '../i18n/knownMessages';
import { paginate, shortOrderId } from '../utils/paginate';
import { googleMapsUrl } from '../utils/mapsLink';
import { formatFulfillmentTime } from '../utils/formatFulfillmentTime';
import { useIsMobile } from '../utils/useIsMobile';

// Button colour per step
const STEP_STYLES = {
    processing: 'bg-yellow-400',
    packed: 'bg-orange-400',
    shipped: 'bg-purple-400',
    out_for_delivery: 'bg-indigo-400',
    delivered: 'bg-green-500 text-white',
    ready_for_pickup: 'bg-teal-500 text-white',
    picked_up: 'bg-green-500 text-white',
};

// Badge colour per order status, matching My Orders so a status looks the same on both pages
const STATUS_BADGE_STYLES = {
    pending: 'bg-gray-100 text-gray-700',
    paid: 'bg-gray-100 text-gray-700',
    processing: 'bg-yellow-100 text-yellow-800',
    packed: 'bg-orange-100 text-orange-800',
    shipped: 'bg-purple-100 text-purple-800',
    out_for_delivery: 'bg-indigo-100 text-indigo-800',
    ready_for_pickup: 'bg-teal-100 text-teal-800',
    delivered: 'bg-green-100 text-green-800',
    picked_up: 'bg-green-100 text-green-800',
};

// Every status an order can be in, for the filter dropdown
const STATUS_FILTERS = [
    'pending', 'paid', 'processing', 'packed', 'shipped', 'out_for_delivery',
    'delivered', 'ready_for_pickup', 'picked_up', 'cancelled',
];

// Underlying value per sortable column, so sorting ignores formatted display text
const SORT_VALUES = {
    orderId: (o) => o._id || '',
    user: (o) => o.userId?.username || '',
    group: (o) => o.groupId?.name || '',
    animal: (o) => o.groupId?.animal || '',
    portion: (o) => o.portion || '',
    phone: (o) => o.customerPhone || '',
    amount: (o) => Number(o.amountPaid || 0) + Number(o.deliveryFee || 0) + Number(o.cuttingPackagingFee || 0),
    type: (o) => o.fulfillmentType || '',
    date: (o) => new Date(o.createdAt).getTime() || 0,
    status: (o) => o.status || '',
};

// Supplier cancellation (with a required reason) and the recording of a paid cash refund, for one order.
// The server decides every rule; these controls only collect the input.
function OrderCancelControls({ order, onCancelled, onSettled }) {
    const { t } = useTranslation();
    const [reasonOpen, setReasonOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [settleOpen, setSettleOpen] = useState(false);
    const [settle, setSettle] = useState({ method: 'cash', reference: '' });
    const [busy, setBusy] = useState(false);
    const supplierId = String(order.supplierId?._id || order.supplierId || '');
    const reasonOk = reason.trim().length >= MIN_SUPPLIER_REASON;

    const submitCancel = async (e) => {
        e.preventDefault();
        if (!reasonOk) return;
        setBusy(true);
        try {
            if (isPerOrderCash(order)) {
                // Only this order: its siblings in the same checkout keep their own payment and stock
                await api.post(`/api/cash/orders/${order._id}/cancel`, { reason: reason.trim() });
                toast.success(t('cancel.done'));
                onCancelled(order.checkoutId, supplierId, order.paymentStatus === 'paid' ? 'manual_due' : 'none', order._id, reason.trim());
            } else {
                const res = await api.post(`/api/orders/groups/${order.checkoutId}/${supplierId}/cancel`, { reason: reason.trim() });
                toast.success(t('cancel.done'));
                onCancelled(order.checkoutId, supplierId, res.data.refund, null, reason.trim());
            }
            setReasonOpen(false);
            setReason('');
        } catch (err) {
            toast.error(translateKnown(t, err.response?.data?.message) || t('cancel.error'));
        } finally {
            setBusy(false);
        }
    };

    const submitSettle = async (e) => {
        e.preventDefault();
        if (!settle.reference.trim()) return;
        setBusy(true);
        try {
            await api.post(`/api/orders/${order._id}/settle-refund`, { method: settle.method, reference: settle.reference.trim() });
            toast.success(t('cancel.settleDone'));
            onSettled(order._id, settle.reference.trim());
            setSettleOpen(false);
        } catch (err) {
            toast.error(translateKnown(t, err.response?.data?.message) || t('cancel.error'));
        } finally {
            setBusy(false);
        }
    };

    const refund = order.refund?.status || 'none';
    const cancelDetails = cancellationDetails(t, order);
    return (
        <div className="space-y-1 text-xs">
            {cancellationStatusKey([order]) && <div className="text-gray-600">{t(cancellationStatusKey([order]))}</div>}
            {cancelDetails && (
                <div className="text-gray-600">
                    <div>{cancelDetails.who}{cancelDetails.when && ` · ${cancelDetails.when}`}</div>
                    <div className="break-words">{cancelDetails.reason}</div>
                </div>
            )}
            {order.refund?.status === 'manual_recorded' && order.refund.reference && (
                <div className="text-gray-600 break-words">{t('cancel.refundReference', { reference: order.refund.reference })}</div>
            )}
            {canSupplierCancel(order) && order.paymentStatus !== 'awaiting_cash' && !reasonOpen && (
                <button type="button" onClick={() => setReasonOpen(true)} className="text-red-600 hover:underline focus:outline-none">{t('cancel.supplierButton')}</button>
            )}
            {reasonOpen && (
                <form onSubmit={submitCancel} className="space-y-1">
                    <label htmlFor={`cancel-reason-${order._id}`} className="block font-medium">{t('cancel.reasonLabel')}</label>
                    <textarea id={`cancel-reason-${order._id}`} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="border p-1 rounded w-full" />
                    <p className="text-gray-500">{t('cancel.reasonHint')}</p>
                    <div className="flex gap-2">
                        <button type="submit" disabled={!reasonOk || busy} className="px-2 py-1 rounded bg-red-600 text-white disabled:opacity-50">{t('cancel.submit')}</button>
                        <button type="button" onClick={() => setReasonOpen(false)} className="px-2 py-1 rounded border border-gray-400">{t('cancel.close')}</button>
                    </div>
                </form>
            )}
            {order.status === 'cancelled' && refund === 'manual_due' && !settleOpen && (
                <button type="button" onClick={() => setSettleOpen(true)} className="text-blue-600 hover:underline focus:outline-none">{t('cancel.settleButton')}</button>
            )}
            {settleOpen && (
                <form onSubmit={submitSettle} className="space-y-1">
                    <label htmlFor={`settle-method-${order._id}`} className="block font-medium">{t('cancel.method')}</label>
                    <select id={`settle-method-${order._id}`} value={settle.method} onChange={(e) => setSettle({ ...settle, method: e.target.value })} className="border p-1 rounded w-full">
                        {['cash', 'bank', 'mobile', 'other'].map((m) => <option key={m} value={m}>{t('cancel.methods.' + m)}</option>)}
                    </select>
                    <label htmlFor={`settle-ref-${order._id}`} className="block font-medium">{t('cancel.reference')}</label>
                    <input id={`settle-ref-${order._id}`} value={settle.reference} onChange={(e) => setSettle({ ...settle, reference: e.target.value })} className="border p-1 rounded w-full" />
                    <div className="flex gap-2">
                        <button type="submit" disabled={!settle.reference.trim() || busy} className="px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-50">{t('cancel.settleSubmit')}</button>
                        <button type="button" onClick={() => setSettleOpen(false)} className="px-2 py-1 rounded border border-gray-400">{t('cancel.close')}</button>
                    </div>
                </form>
            )}
        </div>
    );
}

export default function SupplierOrders() {
  const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [amountOrder, setAmountOrder] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [suppliers, setSuppliers] = useState([]);
    const [supplierFilter, setSupplierFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [userForModal, setUserForModal] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [expanded, setExpanded] = useState(() => new Set()); // mobile cards only
    const isMobile = useIsMobile();

    const currentUser = getCurrentUser();

    // Comma-joined string so the effect only re-runs when the linked suppliers actually change
    const supplierKey = (currentUser?.supplierIds || []).join(',');
    // Super admins view every supplier's orders read-only; the server allows the same reads
    const isReadOnly = currentUser?.role === 'superadmin';

    useEffect(() => {
        if (!supplierKey && !isReadOnly) return;

        const fetchOrders = async () => {
            try {
                setLoading(true);
                let ids = supplierKey.split(',');
                if (isReadOnly) {
                    const list = (await api.get('/api/suppliers')).data;
                    setSuppliers(list);
                    ids = list.map((s) => s._id);
                }
                // Orders from every supplier in scope, newest first
                const results = await Promise.all(
                    ids.map((id) => api.get(`/api/shipping/supplier/${id}`))
                );
                const merged = results.flatMap((r) => r.data);
                merged.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setOrders(merged);
            } catch (err) {
                console.error('❌ Failed to fetch supplier orders', err);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [supplierKey, isReadOnly]);

    // A cash checkout is one supplier's part of a batch: its orders share checkoutId. Confirming or cancelling
    // changes only those orders, and the supplier cannot move them forward until the cash is confirmed.
    const setCheckoutOrders = (checkoutId, change) => {
        setOrders((prev) => prev.map((o) => (o.checkoutId === checkoutId && o.paymentStatus === 'awaiting_cash' ? { ...o, ...change } : o)));
    };

    // The orders of one supplier's part of a checkout (the same group the server acts on for a checkout-level action)
    const groupOf = (o) => orders.filter((x) => String(x.checkoutId) === String(o.checkoutId)
        && String(x.supplierId?._id || x.supplierId) === String(o.supplierId?._id || o.supplierId));
    // A legacy checkout with several orders is confirmed or cancelled as a whole: its buttons say so and name the scope
    const checkoutWide = (o) => !isPerOrderCash(o) && o.paymentMethod === 'cash' && groupOf(o).length > 1;
    const needsReconcile = (o) => legacyGroupNeedsReconcile(groupOf(o));

    // Per-order checkouts confirm and cancel one order; older checkouts still act on all of their unpaid orders
    const confirmCash = async (o) => {
        try {
            if (isPerOrderCash(o)) {
                await api.post(`/api/cash/orders/${o._id}/confirm`);
                setOrders((prev) => prev.map((x) => (x._id === o._id ? { ...x, paymentStatus: 'paid' } : x)));
            } else {
                if (checkoutWide(o) && !window.confirm(t('cash.confirmCheckoutScope', { count: groupOf(o).length, defaultValue: 'Mark cash received for all {{count}} orders of this supplier in this checkout?' }))) return;
                await api.post(`/api/cash/checkouts/${o.checkoutId}/confirm`);
                setCheckoutOrders(o.checkoutId, { paymentStatus: 'paid' });
            }
            toast.success(t('cash.confirmed'));
        } catch (err) {
            toast.error(translateKnown(t, err.response?.data?.message) || t('cash.errorConfirm'));
        }
    };

    const cancelCashOrder = async (o) => {
        if (isPerOrderCash(o) || !checkoutWide(o)) {
            if (!window.confirm(t('cash.confirmCancel'))) return;
        } else if (!window.confirm(t('cash.confirmCancelCheckoutScope', { count: groupOf(o).length, defaultValue: 'Cancel all {{count}} orders of this supplier in this checkout? Unpaid orders are released; no payment is refunded here.' }))) {
            return;
        }
        try {
            if (isPerOrderCash(o)) {
                await api.post(`/api/cash/orders/${o._id}/cancel`, {});
                setOrders((prev) => prev.map((x) => (x._id === o._id ? { ...x, status: 'cancelled', paymentStatus: 'cancelled' } : x)));
            } else {
                await api.post(`/api/cash/checkouts/${o.checkoutId}/cancel`);
                setCheckoutOrders(o.checkoutId, { status: 'cancelled', paymentStatus: 'cancelled' });
            }
            toast.success(t('cash.cancelled'));
        } catch (err) {
            toast.error(translateKnown(t, err.response?.data?.message) || t('cash.errorCancel'));
        }
    };

    // After a cancel: every order of that supplier's group in this checkout shows as cancelled, with the refund that followed.
    // A per-order cancel (orderId given) changes only that order.
    // The cancellation record is set locally from what was just submitted (the server stamps the same reason and time)
    const markGroupCancelled = (checkoutId, supplierId, refundStatus, orderId = null, reason = null) => {
        const cancellation = { by: 'supplier', reason, at: new Date().toISOString() };
        setOrders((prev) => prev.map((x) => (
            (orderId ? x._id === orderId : String(x.checkoutId) === String(checkoutId) && String(x.supplierId?._id || x.supplierId) === String(supplierId))
                ? { ...x, status: 'cancelled', paymentStatus: x.paymentStatus === 'awaiting_cash' ? 'cancelled' : x.paymentStatus, cancellation, refund: { ...(x.refund || {}), status: refundStatus } }
                : x
        )));
    };

    const markSettled = (orderId, reference) => {
        setOrders((prev) => prev.map((x) => (x._id === orderId ? { ...x, refund: { ...(x.refund || {}), status: 'manual_recorded', reference } } : x)));
    };

    const updateStatus = async (orderId, status) => {
        try {
            const res = await api.put(`/api/shipping/${orderId}/status`, { status });

            // update UI instantly
            setOrders(prev =>
                prev.map(o => o._id === orderId ? res.data : o)
            );
        } catch (err) {
            console.error('❌ Failed to update status', err);
            toast.error(err.response?.data?.message || t('supplierOrders.updateFailed'));
        }
    };

    // Orders carry the supplier as an ID (unpopulated); names come from the already-loaded supplier list
    const supplierIdOf = (o) => String(o.supplierId?._id || o.supplierId || '');
    const supplierNames = useMemo(() => new Map(suppliers.map((s) => [String(s._id), s.displayName || s.name])), [suppliers]);

    const filteredOrders = useMemo(
        () => orders.filter((o) => (statusFilter === 'all' || o.status === statusFilter)
            && (!isReadOnly || supplierFilter === 'all' || supplierIdOf(o) === supplierFilter)),
        [orders, statusFilter, supplierFilter, isReadOnly]
    );
    const sortedOrders = useMemo(() => {
        if (!sortConfig.key) return filteredOrders;
        const dir = sortConfig.direction === 'asc' ? 1 : -1;
        const valueOf = SORT_VALUES[sortConfig.key];
        return [...filteredOrders].sort((a, b) => {
            const av = valueOf(a);
            const bv = valueOf(b);
            if (av < bv) return -dir;
            if (av > bv) return dir;
            return 0;
        });
    }, [filteredOrders, sortConfig]);

    const sortByKey = (key) => {
        const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
    };
    const sortArrow = (key) => <span className="inline-block w-3 text-center">{sortConfig.key !== key ? '' : sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;

    const { pageItems, page: currentPage, pageCount } = paginate(sortedOrders, page);

    // The date and time the order reached its final step
    const finishedDate = (o) => {
        const when = o.status === 'picked_up' ? o.pickedUpAt : o.deliveredAt;
        return formatFulfillmentTime(when);
    };

    const toggleExpanded = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Payment, fulfilment and cancellation controls for one order; shared by the desktop row and the mobile card
    const renderActions = (o) => {
        const finished = isFinished(o.fulfillmentType, o.status);
        return (
            <>
                <div className="flex flex-wrap gap-2">
                {o.paymentStatus === 'awaiting_cash' && needsReconcile(o) ? (
                    <span className="text-xs text-amber-700">{t('cash.legacyReconcile', { defaultValue: 'Made before per-order cash payments. It needs manual reconciliation, so its orders cannot be changed here.' })}</span>
                ) : o.paymentStatus === 'awaiting_cash' ? (
                    <>
                        <button type="button" onClick={() => confirmCash(o)} className="px-2 py-1 rounded text-xs bg-green-600 text-white hover:bg-green-700">
                            {checkoutWide(o) ? t('cash.confirmCheckout', { defaultValue: 'Confirm cash for checkout' }) : t('cash.confirm')}
                        </button>
                        <button type="button" onClick={() => cancelCashOrder(o)} className="px-2 py-1 rounded text-xs border border-gray-400 text-gray-700 hover:bg-gray-100">
                            {checkoutWide(o) ? t('cash.cancelCheckout', { defaultValue: 'Cancel checkout' }) : t('cash.cancelOrder')}
                        </button>
                    </>
                ) : finished ? (
                    <span className="text-xs text-gray-500">{t('supplierOrders.noChanges')}</span>
                ) : (
                    nextSteps(o.fulfillmentType, o.status).map(step => (
                        <button
                            key={step}
                            onClick={() => updateStatus(o._id, step)}
                            className={`px-2 py-1 rounded text-xs ${STEP_STYLES[step]}`}
                        >
                            {t('supplierOrders.step.' + step)}
                        </button>
                    ))
                )}
                </div>
                <div className="mt-2">
                    {!needsReconcile(o) && <OrderCancelControls order={o} onCancelled={markGroupCancelled} onSettled={markSettled} />}
                </div>
            </>
        );
    };

    // Mobile card: identity, amount and status always visible; contact, fulfilment and actions under the expand control
    const renderMobileCard = (o) => {
        const isOpen = expanded.has(o._id);
        const finished = isFinished(o.fulfillmentType, o.status);
        const amount = Number(o.amountPaid || 0) + Number(o.deliveryFee || 0) + Number(o.cuttingPackagingFee || 0);
        const supplierName = supplierNames.get(supplierIdOf(o));
        const fulfillment = o.fulfillmentType ? t('orders.fulfillment.' + o.fulfillmentType, { defaultValue: o.fulfillmentType }) : '';
        return (
            <li key={o._id} className="min-w-0 overflow-hidden border rounded-md bg-white shadow-sm p-3 text-sm">
                <div className="flex items-start gap-2 min-w-0">
                    <div className="min-w-0 flex-1">
                        <div className="text-xs font-mono text-gray-700 truncate" title={o._id}>{shortOrderId(o._id)}</div>
                        {isReadOnly && <div className="text-gray-600 truncate" title={supplierName}>{supplierName || t('common.na')}</div>}
                        <div className="font-medium text-gray-800 truncate" title={o.groupId?.name}>{o.groupId?.name || t('common.na')}</div>
                        <div className="truncate">
                            {o.userId?.username ? (
                                <button type="button" onClick={() => setUserForModal(o.userId)} className="text-blue-600 hover:underline focus:outline-none max-w-full truncate" title={t('supplierOrders.viewUser')}>{o.userId.username}</button>
                            ) : t('common.na')}
                        </div>
                    </div>
                    <button type="button" onClick={() => toggleExpanded(o._id)} aria-expanded={isOpen} className="shrink-0 p-1 text-blue-600 focus:outline-none">
                        <i className={`fas ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true" />
                    </button>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <button type="button" onClick={() => setAmountOrder(o)} className="font-semibold text-blue-600 hover:underline focus:outline-none">${amount.toFixed(2)}</button>
                    <span className={`px-2 py-1 rounded text-sm whitespace-nowrap ${STATUS_BADGE_STYLES[o.status] || STATUS_BADGE_STYLES.pending}`}>
                        {t('orders.status.' + (o.status || 'unknown'), { defaultValue: (o.status || 'unknown').split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') })}
                    </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                    <span className="min-w-0 break-words">{animalLabel(t, o.groupId?.animal) || t('common.na')}</span>
                    <span>{o.portion ? t('portions.' + String(o.portion).toLowerCase(), { defaultValue: o.portion }) : o.portion}</span>
                    <span>{new Date(o.createdAt).toLocaleDateString()}</span>
                </div>
                {isOpen && (
                    <div className="mt-3 border-t border-gray-200 pt-2 min-w-0 space-y-2">
                        <div className="text-xs">
                            <span className="font-medium text-gray-700">{t('supplierOrders.cols.phone')}: </span>
                            {o.customerPhone ? <a href={`tel:${o.customerPhone.replace(/[^\d+]/g, '')}`} className="text-blue-600 hover:underline break-all">{o.customerPhone}</a> : t('common.na')}
                        </div>
                        <div className="text-xs capitalize min-w-0">
                            <span className="font-medium text-gray-700 normal-case">{t('supplierOrders.cols.type')}: </span>{fulfillment}
                            {o.fulfillmentType === 'delivery' && o.deliveryAddress && (
                                <a href={googleMapsUrl(o.deliveryAddress)} target="_blank" rel="noopener noreferrer" className="block normal-case text-blue-600 underline break-words">
                                    {[o.deliveryAddress.street, o.deliveryAddress.city, stateLabel(o.deliveryAddress.state), o.deliveryAddress.zip].filter(Boolean).join(', ')}
                                </a>
                            )}
                        </div>
                        {finished && finishedDate(o) && (
                            <div className="text-xs text-gray-500">{o.status === 'picked_up' ? t('orders.pickedUp') : t('orders.delivered')} {finishedDate(o)}</div>
                        )}
                        {o.paymentStatus === 'awaiting_cash' && (
                            <div className="text-xs text-amber-700">{t('cash.dueBy', { deadline: formatDeadline(o.cashDeadline) })}</div>
                        )}
                        {!isReadOnly && renderActions(o)}
                    </div>
                )}
            </li>
        );
    };

    return (
        <>
        <AdminNav />
        <div className="max-w-7xl mx-auto bg-white shadow p-6 rounded-md">
            <h1 className="text-2xl font-semibold text-gray-800 mb-4">{t('supplierOrders.title')}</h1>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-sm">
                {/* Each label stays with its control: stacked on mobile, side by side from sm up */}
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <label htmlFor="status-filter" className="font-medium">{t('supplierOrders.statusLabel')}</label>
                <select
                    id="status-filter"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    className="border p-1 rounded"
                >
                    <option value="all">{t('supplierOrders.allStatuses')}</option>
                    {STATUS_FILTERS.map(s => (
                        <option key={s} value={s}>{t('orders.status.' + s)}</option>
                    ))}
                </select>
                </div>
                {isReadOnly && (
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                        <label htmlFor="supplier-filter" className="font-medium">{t('supplierOrders.supplier', { defaultValue: t('supplierOrders.supplier')  })}</label>
                        <select
                            id="supplier-filter"
                            value={supplierFilter}
                            onChange={(e) => { setSupplierFilter(e.target.value); setPage(1); }}
                            className="border p-1 rounded max-w-full"
                        >
                            <option value="all">{t('supplierOrders.allSuppliers', { defaultValue: t('supplierOrders.allSuppliers')  })}</option>
                            {suppliers.map(s => (
                                <option key={s._id} value={String(s._id)}>{s.displayName || s.name}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {loading ? (
                <p>{t('supplierOrders.loading')}</p>
            ) : filteredOrders.length === 0 ? (
                <p className="text-gray-500">{t('supplierOrders.noOrders')}</p>
            ) : (
                <>
                <ResultCount total={sortedOrders.length} page={currentPage} />
                {isMobile ? (
                <ul className="space-y-3">{pageItems.map(renderMobileCard)}</ul>
                ) : (
                <div className="overflow-x-auto">
                <table className="w-full min-w-[64rem] bg-white border rounded shadow">
                    <thead>
                        <tr className="bg-gray-200 text-sm">
                            <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('orderId')}>{t('supplierOrders.cols.orderId')}{sortArrow('orderId')}</th>
                            {isReadOnly && <th className="p-3 text-left">{t('supplierOrders.cols.supplier', { defaultValue: 'Supplier' })}</th>}
                            <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('user')}>{t('supplierOrders.cols.user')}{sortArrow('user')}</th>
                            <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('group')}>{t('supplierOrders.cols.group')}{sortArrow('group')}</th>
                            <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('animal')}>{t('supplierOrders.cols.animal')}{sortArrow('animal')}</th>
                            <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('portion')}>{t('supplierOrders.cols.portion')}{sortArrow('portion')}</th>
                            <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('phone')}>{t('supplierOrders.cols.phone')}{sortArrow('phone')}</th>
                            <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('amount')}>{t('supplierOrders.cols.amount')}{sortArrow('amount')}</th>
                            <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('type')}>{t('supplierOrders.cols.type')}{sortArrow('type')}</th>
                            <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('date')}>{t('supplierOrders.cols.date')}{sortArrow('date')}</th>
                            <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('status')}>{t('supplierOrders.cols.status')}{sortArrow('status')}</th>
                            {!isReadOnly && <th className="p-3 text-left">{t('supplierOrders.cols.actions')}</th>}
                        </tr>
                    </thead>

                    <tbody>
                        {pageItems.map(o => {
                            const finished = isFinished(o.fulfillmentType, o.status);
                            return (
                                <tr key={o._id} className="text-left border-t hover:bg-gray-50 align-top">
                                    <td className="p-3 text-xs font-mono" title={o._id}>
                                        <div>{shortOrderId(o._id)}</div>
                                        {o.checkoutId && <div className="text-[10px] text-gray-500 mt-0.5" title={t('supplierOrders.checkoutTitle', { id: shortOrderId(o.checkoutId) })}>{t('supplierOrders.checkout', { id: shortOrderId(o.checkoutId) })}</div>}
                                    </td>
                                    {isReadOnly && <td className="p-3 text-sm">{supplierNames.get(supplierIdOf(o)) || t('common.na')}</td>}
                                    <td className="p-3">
                                        {o.userId?.username ? (
                                            <button type="button" onClick={() => setUserForModal(o.userId)} className="text-blue-600 hover:underline focus:outline-none" title={t('supplierOrders.viewUser')}><TruncatedText text={o.userId.username} maxWidthClass="max-w-[10rem]" /></button>
                                        ) : t('common.na')}
                                    </td>
                                    <td className="p-3"><TruncatedText text={o.groupId?.name || t('common.na')} /></td>
                                    <td className="p-3">{animalLabel(t, o.groupId?.animal) || t('common.na')}</td>
                                    <td className="p-3">{o.portion ? t('portions.' + String(o.portion).toLowerCase(), { defaultValue: o.portion }) : o.portion}</td>
                                    <td className="p-3 text-sm break-all">{o.customerPhone ? <a href={`tel:${o.customerPhone.replace(/[^\d+]/g, '')}`} className="text-blue-600 hover:underline">{o.customerPhone}</a> : t('common.na')}</td>
                                    <td className="p-3">
                                        <button type="button" onClick={() => setAmountOrder(o)} className="text-blue-600 hover:underline focus:outline-none">
                                            ${(Number(o.amountPaid || 0) + Number(o.deliveryFee || 0) + Number(o.cuttingPackagingFee || 0)).toFixed(2)}
                                        </button>
                                                                            </td>
                                    <td className="p-3 capitalize">
                                        {o.fulfillmentType ? t('orders.fulfillment.' + o.fulfillmentType, { defaultValue: o.fulfillmentType }) : ''}
                                        {o.fulfillmentType === 'delivery' && o.deliveryAddress && (
                                            <a
                                                href={googleMapsUrl(o.deliveryAddress)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block text-xs normal-case text-blue-600 underline"
                                            >
                                                {[o.deliveryAddress.street, o.deliveryAddress.city, stateLabel(o.deliveryAddress.state), o.deliveryAddress.zip].filter(Boolean).join(', ')}
                                            </a>
                                        )}
                                    </td>
                                    <td className="p-3 text-sm">{new Date(o.createdAt).toLocaleDateString()}</td>

                                    <td className="p-3">
                                        <span className={`px-2 py-1 rounded text-sm whitespace-nowrap ${STATUS_BADGE_STYLES[o.status] || STATUS_BADGE_STYLES.pending}`}>
                                            {t('orders.status.' + (o.status || 'unknown'), { defaultValue: (o.status || 'unknown').split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') })}
                                        </span>
                                        {finished && finishedDate(o) && (
                                            <div className="text-xs text-gray-500 mt-1">
                                                {o.status === 'picked_up' ? t('orders.pickedUp') : t('orders.delivered')} {finishedDate(o)}
                                            </div>
                                        )}
                                        {o.paymentStatus === 'awaiting_cash' && (
                                            <div className="text-xs text-amber-700 mt-1">{t('cash.dueBy', { deadline: formatDeadline(o.cashDeadline) })}</div>
                                        )}
                                    </td>

                                    {!isReadOnly && <td className="p-3">{renderActions(o)}</td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
                )}
                {amountOrder && <OrderAmountModal order={amountOrder} onClose={() => setAmountOrder(null)} />}
                <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />
                </>
            )}
            <UserInfoModal isOpen={!!userForModal} onClose={() => setUserForModal(null)} user={userForModal} />
        </div>
        </>
    );
}
